import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <img
            src="https://customer-assets.emergentagent.com/job_freight-flow-51/artifacts/jlvfmutc_logo%20files.png"
            alt="Primeswift Solutions"
            className="h-12 w-auto"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using the services of Primeswift Solutions Pvt Ltd ("Primeswift," "we," "us," or "our"), 
                you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Description</h2>
              <p className="text-gray-700 mb-3">Primeswift provides logistics services including:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Freight and transportation services</li>
                <li>Warehousing and storage solutions</li>
                <li>Last-mile delivery services</li>
                <li>Supply chain management</li>
                <li>Fleet partner network services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Clients</h3>
              <p className="text-gray-700 mb-3">As a client, you agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide accurate shipment information</li>
                <li>Ensure cargo complies with legal regulations</li>
                <li>Make timely payments for services rendered</li>
                <li>Properly package and label shipments</li>
                <li>Declare hazardous materials appropriately</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Fleet Partners</h3>
              <p className="text-gray-700 mb-3">As a fleet partner, you agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Maintain valid licenses and permits</li>
                <li>Ensure vehicle safety and maintenance</li>
                <li>Provide accurate vehicle information</li>
                <li>Comply with traffic and transportation laws</li>
                <li>Maintain appropriate insurance coverage</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Pricing and Payment</h2>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>All prices are quoted in Indian Rupees (INR)</li>
                <li>Quotes are valid for 7 days unless otherwise stated</li>
                <li>Payment terms will be specified in service agreements</li>
                <li>Late payment may incur additional charges</li>
                <li>We reserve the right to modify pricing with notice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Liability and Insurance</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Cargo Liability</h3>
              <p className="text-gray-700 mb-4">
                Primeswift maintains cargo insurance as per industry standards. Claims must be filed within 7 days 
                of delivery or scheduled delivery date. Maximum liability is limited to the declared value of goods.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Limitations</h3>
              <p className="text-gray-700 mb-3">We are not liable for:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Delays due to force majeure events</li>
                <li>Improper packaging by the shipper</li>
                <li>Undeclared or misdeclared cargo</li>
                <li>Inherent defects in goods</li>
                <li>Acts of God, riots, strikes, or natural disasters</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Service Cancellation</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 By Client</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Cancellation more than 24 hours before pickup: No charge</li>
                <li>Cancellation within 24 hours: 25% of service fee</li>
                <li>No-show or refusal to load: 50% of service fee</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 By Primeswift</h3>
              <p className="text-gray-700 mb-4">
                We reserve the right to refuse or cancel service if cargo is found to be prohibited, dangerous, 
                or not as declared.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Prohibited Items</h2>
              <p className="text-gray-700 mb-3">The following items are prohibited unless proper authorization is obtained:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Explosives and hazardous materials (without proper declaration)</li>
                <li>Illegal drugs and contraband</li>
                <li>Live animals (except with special arrangements)</li>
                <li>Perishable goods (without temperature control arrangement)</li>
                <li>Items prohibited by law</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                Any disputes arising from these terms shall be resolved through arbitration in Pune, India, 
                in accordance with the Arbitration and Conciliation Act, 1996.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All content on our website, including logos, text, images, and software, is the property of 
                Primeswift Solutions Pvt Ltd and protected by copyright laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Data Protection</h2>
              <p className="text-gray-700 mb-4">
                Your personal data will be processed in accordance with our Privacy Policy. By using our services, 
                you consent to such processing.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Force Majeure</h2>
              <p className="text-gray-700 mb-4">
                Primeswift shall not be liable for delays or failures in performance resulting from acts beyond 
                our reasonable control, including natural disasters, strikes, government actions, or pandemics.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Modifications to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                immediately upon posting on our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of India. 
                Courts in Pune, Maharashtra shall have exclusive jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Severability</h2>
              <p className="text-gray-700 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining provisions shall 
                continue in full force and effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                <p className="text-gray-700 mb-2"><strong>Primeswift Solutions Pvt Ltd</strong></p>
                <p className="text-gray-700 mb-1">C-425, Gera's Imperium Gateway</p>
                <p className="text-gray-700 mb-1">Bhosari Metro Station, Pune - 411034</p>
                <p className="text-gray-700 mb-1">Phone: +91-9579027425</p>
                <p className="text-gray-700 mb-3">Email: info@primeswift.in</p>
                <p className="text-gray-600 text-sm mt-4">
                  For any questions regarding these Terms of Service, please contact us at the above details.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
